import { Result, Button, Typography } from 'antd';
import { Navigate, useNavigate, useRouteError } from 'react-router';

import { FullScreenSpin } from '@/components/FullScreenSpin';
import { useAuth } from '@/features/auth/authContext';
import { ru } from '@/i18n/ru';
import { NotFoundPage } from '@/features/placeholder/PlaceholderPage';

import { homePath } from './navigation';

/** "/" sends each role to its first section. */
export function HomeRedirect() {
  const { state } = useAuth();
  if (state.status === 'loading') return <FullScreenSpin />;
  if (state.status !== 'authenticated') return <Navigate to="/login" replace />;
  return <Navigate to={homePath(state.user.role)} replace />;
}

/** Signed in, and nothing to open: says so, with the way out. */
export function NoAccess() {
  const { state, logout } = useAuth();
  if (state.status === 'loading') return <FullScreenSpin />;
  if (state.status !== 'authenticated') return <Navigate to="/login" replace />;
  const role = state.user.role;
  return (
    <Result
      status="403"
      title={ru.noAccess.title}
      subTitle={ru.noAccess.hint((ru.roles as Record<string, string>)[role] ?? role)}
      extra={
        <Button type="primary" onClick={() => void logout()}>
          {ru.header.logout}
        </Button>
      }
    />
  );
}

export function NotFound() {
  const navigate = useNavigate();
  return <NotFoundPage onHome={() => void navigate('/')} />;
}


/** What a page shows when its own rendering throws (STAGE_3 3.5).
 *
 * Registered as the `errorElement` of every section, so the failure stays inside the
 * layout: the menu still works and the rest of the app is still reachable. Without it
 * react-router replaces the whole document with its own stack trace, which is the right
 * thing for a developer and no use at all to an operator mid-shift.
 */
export function RouteError() {
  const error = useRouteError();
  const text = error instanceof Error ? error.message : String(error);
  return (
    <Result
      status="error"
      title={ru.errors.pageFailed}
      subTitle={<Typography.Text type="secondary">{text}</Typography.Text>}
      extra={
        <Button
          type="primary"
          onClick={() => {
            window.location.reload();
          }}
        >
          {ru.errors.reload}
        </Button>
      }
    />
  );
}
